import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/data/categories";
import { useClickOutside } from "@/hooks/useClickOutside";

export default function CategorySection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useClickOutside(dropdownRef, () => setOpen(false));

  return (
    <section id="categories" className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-2xl font-bold text-foreground-950 md:text-4xl">{t("categories.title")}</h2>
        <p className="mt-3 text-base leading-relaxed text-foreground-600">{t("categories.subtitle")}</p>
      </div>

      <div className="relative mx-auto mt-8 max-w-sm" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
        >
          <span className="flex items-center gap-2">
            <i className="ri-apps-2-line text-base text-primary-600" />
            {t("categories.dropdownPlaceholder")}
          </span>
          <i className={`ri-arrow-down-s-line text-base transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div className="absolute z-20 mt-2 max-h-80 w-full overflow-y-auto rounded-lg border border-background-200 bg-background-50">
            {categories.map((cat) => (
              <a
                key={cat.key}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setOpen(false)}
                className="flex cursor-pointer items-center justify-between gap-2 whitespace-nowrap px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100"
              >
                <span>{t(`categories.${cat.key}`)}</span>
                <i className="ri-arrow-right-up-line text-sm text-foreground-400" />
              </a>
            ))}
          </div>
        )}
      </div>

      <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7">
        {categories.map((cat) => (
          <a
            key={cat.key}
            href={cat.url}
            target="_blank"
            rel="noopener nofollow"
            className="group cursor-pointer overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
          >
            <div className="aspect-square w-full overflow-hidden">
              <img
                src={cat.image}
                alt={`${t(`categories.${cat.key}`)} — USFans Spreadsheet category`}
                className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
              />
            </div>
            <div className="flex items-center justify-between gap-1 px-3 py-2.5">
              <span className="truncate text-sm font-medium text-foreground-800">{t(`categories.${cat.key}`)}</span>
              <i className="ri-arrow-right-up-line shrink-0 text-sm text-foreground-400 transition-colors group-hover:text-primary-600" />
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}